import React from "react";
import iconInsight from "../../../assets/brand/icon-insight.svg";
import AthleteHoverCard from "./AthleteHoverCard";
import { EMPTY_VALUE, NO_30D_HISTORY, fmtCompact, fmtMoney, fmtMoneyCompact, fmtSigned, initialsOf, plural } from "./coachUtils";
import { timeAgo } from "../../ForBrands/brandDealUtils";
import { Avatar, Badge, Btn, Card, InsightBox, MetricCard, Overline, Tile } from "../../ForBrands/ui/BrandUI";
import { MobileKpiTile, StatList, TextLink } from "../../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
// The phone card chrome (2406:881, 2406:932, 2406:1035, 2406:1076).
import { PHONE_CARD } from "./coachPieces";

// Coach Mode · Home cards (Figma 01 · Home, 1371:1326). Each card renders
// one part of the coach/home payload. A figure the product has no data for
// shows the en dash placeholder or a plain note, never a made-up number.
//
// Inside the Coach Mode shell below 640px (usePhoneTheme) each card renders
// its phone layout (Figma COACH MODE V2 mobile, 01 · Home 2406:745): the
// 16px phone card with the subtle border, smaller type, text links in place
// of the small buttons. The data and copy are the same on every width.

const cx = (...parts) => parts.filter(Boolean).join(" ");

// "+3.2 in 30d", "+$3.2K in 30d", or the note until 30 days of history exist.
const change30d = (n, fmt) => (n == null ? NO_30D_HISTORY : `${fmtSigned(n, fmt)} in 30d`);

// The national average is each ranked player's own group's (sport, level
// and gender: users/national_cohorts.py), so it is null when no activated
// player's group is large enough to rank, and `basis` names the group when
// the roster sits in one ("College women's basketball").
export const nationalComparison = (avg, national, basis) => {
  if (avg == null) return "Activate players to compare nationally";
  if (national == null) return "No national average for this group yet";
  const delta = Math.round((avg - national) * 10) / 10;
  const group = String(basis || "").trim();
  const average = group ? `the ${group.charAt(0).toLowerCase()}${group.slice(1)} average` : "national average";
  return delta >= 0 ? `+${delta} pts above ${average}` : `${Math.abs(delta)} pts below ${average}`;
};

// A phone KPI tile is half the screen wide, so at 360px a seven-figure
// total in full ("$1,275,000") breaks across two lines mid-number. Phones
// show $1M and up compact ("$1.28M", the Team snapshot format); smaller
// totals stay in full, as in the frame ("$127,500").
const PHONE_COMPACT_FROM = 1_000_000;
const phoneMoney = (n) => (Math.abs(Number(n)) >= PHONE_COMPACT_FROM ? fmtMoneyCompact(n) : fmtMoney(n));

// KPI strip. Phones (2406:825): a 2 x 2 grid of KPI tiles, the second one
// muted as on desktop.
export const HomeKpis = ({ kpis, totals }) => {
  const phone = usePhoneTheme();
  const tiles = [
    { label: "Avg CacheScore", value: kpis.avgScore ?? EMPTY_VALUE, sub: nationalComparison(kpis.avgScore, kpis.nationalAvgScore, kpis.nationalAvgBasis) },
    { label: "Pending approvals", value: kpis.pendingApprovals ?? 0, sub: "Offers and contracts awaiting a response", muted: true },
    {
      label: "Total team NIL closed",
      value: phone ? phoneMoney(kpis.teamNilValue) : fmtMoney(kpis.teamNilValue),
      sub: `${plural(kpis.activeDeals ?? 0, "active deal")} · ${fmtMoney(kpis.paidOut)} paid out`,
    },
    { label: "NIL readiness", value: `${kpis.readinessLinked ?? 0} of ${totals.total ?? 0}`, sub: "accounts linked" },
  ];
  if (phone) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {tiles.map((t) => (
          <MobileKpiTile key={t.label} label={t.label} value={t.value} sub={t.sub} muted={t.muted} />
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-4">
      {tiles.map((t) => (
        <MetricCard key={t.label} muted={t.muted} label={t.label} value={t.value} sub={t.sub} />
      ))}
    </div>
  );
};

// Roster analysis. Phones (2406:855) split it: the compact insight with the
// text, then the three figures as a stat list card of their own.
export const RosterAnalysis = ({ analysis, kpis, snapshot, totals }) => {
  const phone = usePhoneTheme();
  const total = totals.total ?? 0;
  const complete = snapshot.profilesComplete ?? 0;
  const incomplete = Math.max(0, total - complete);
  const tiles = [
    { label: "Activated", value: `${totals.activated ?? 0} of ${total}`, sub: `${totals.pending ?? 0} pending` },
    { label: "Avg. CacheScore™", value: kpis.avgScore ?? EMPTY_VALUE, sub: change30d(analysis.avgScoreDelta30d) },
    { label: "Profiles complete", value: `${complete} of ${total}`, sub: `${incomplete} ${incomplete === 1 ? "needs" : "need"} attention` },
  ];
  const title = `Roster analysis · ${plural(total, "athlete")}`;
  if (phone) {
    return (
      <div className="flex flex-col gap-[10px]">
        <InsightBox compact title={title}>
          <p>{analysis.text}</p>
        </InsightBox>
        <StatList rows={tiles} />
      </div>
    );
  }
  return (
    <InsightBox title={title}>
      <p>{analysis.text}</p>
      <div className="mt-[18px] flex flex-col gap-[14px] sm:flex-row">
        {tiles.map((t) => (
          <Tile key={t.label} label={t.label} value={t.value} sub={t.sub} />
        ))}
      </div>
    </InsightBox>
  );
};

// Only reminders are badged; a message leaves no badge, and nothing
// records replies (contacts are mail drafts), so "Replied" never shows.
const contactBadge = (c) => (c.kind === "notify" ? "Reminded" : c.lastContact ? null : "No contact");

// Tapping an athlete opens a mail draft (`onMessage`); the hover card shows
// on hover and on focus.
const ContactPerson = ({ c, playersById, archetypes, teamName, onMessage, className }) => (
  <AthleteHoverCard player={playersById[c.athleteId]} archetype={archetypes[c.athleteId]} teamName={teamName} className={className}>
    <button type="button" onClick={() => onMessage(c)} className="flex min-w-0 items-center gap-3 text-left hover:opacity-80">
      <Avatar src={c.avatar} initials={initialsOf(c)} size={32} tone="outline" />
      <span className="flex min-w-0 flex-col">
        <span className="bm-h-xs text-bm-ink">
          {c.name} {c.surname}
        </span>
        <span className="bm-body-s text-bm-muted">{c.subtitle || EMPTY_VALUE}</span>
      </span>
    </button>
  </AthleteHoverCard>
);

const EMPTY_CONTACTS = "Registered players show up here once they join your roster.";

// Phones (2406:881): the overline and a "My Circle" text link over a rule,
// then the rows with the badge and a 10px time.
const RecentContactPhone = ({ contacts, playersById, archetypes, teamName, onMessage }) => (
  <section aria-label="Recent athlete contact" className={cx(PHONE_CARD, "flex min-w-0 flex-col pb-1 pt-[14px]")}>
    <div className="flex items-center justify-between gap-3 border-b border-bm-mutedbg pb-[10px]">
      <Overline>Recent athlete contact</Overline>
      <TextLink to="/coach/circle">My Circle</TextLink>
    </div>
    {contacts.length === 0 && <p className="py-3 bm-body-m text-bm-muted">{EMPTY_CONTACTS}</p>}
    <ul>
      {contacts.map((c) => {
        const badge = contactBadge(c);
        return (
          <li key={c.email} className="flex items-center justify-between gap-3 border-b border-bm-mutedbg py-3 last:border-b-0">
            <ContactPerson
              c={c}
              playersById={playersById}
              archetypes={archetypes}
              teamName={teamName}
              onMessage={onMessage}
              className="relative flex min-w-0 break-words"
            />
            <span className="flex shrink-0 items-center gap-2">
              {badge && <Badge tone="outline">{badge}</Badge>}
              <span className="whitespace-nowrap bm-body-xs text-bm-tertiary">{c.lastContact ? timeAgo(c.lastContact) : "Never"}</span>
            </span>
          </li>
        );
      })}
    </ul>
  </section>
);

export const RecentContactCard = (props) => {
  const phone = usePhoneTheme();
  if (phone) return <RecentContactPhone {...props} />;
  const { contacts, playersById, archetypes, teamName, onMessage } = props;
  return (
    <Card as="section" aria-label="Recent athlete contact" padding="p-[22px]" className="flex min-w-0 flex-col">
      <div className="flex items-center justify-between pb-2">
        <Overline>Recent athlete contact</Overline>
        <Btn variant="ghost" size="sm" to="/coach/circle">
          My Circle
        </Btn>
      </div>
      {contacts.length === 0 && <p className="py-3 bm-body-m text-bm-muted">{EMPTY_CONTACTS}</p>}
      <ul>
        {contacts.map((c) => {
          const badge = contactBadge(c);
          return (
            <li key={c.email} className="flex items-center justify-between gap-3 border-b border-bm-mutedbg py-3 last:border-b-0">
              <ContactPerson c={c} playersById={playersById} archetypes={archetypes} teamName={teamName} onMessage={onMessage} />
              <span className="flex shrink-0 items-center gap-[10px]">
                {badge && <Badge tone="outline">{badge}</Badge>}
                <span className="bm-body-s text-bm-tertiary">{c.lastContact ? timeAgo(c.lastContact) : "Never"}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

const Tertiary = ({ children }) => <span className="text-bm-tertiary">{children}</span>;

// The four snapshot figures; the W-L record takes the third place once the
// team has one, else Profiles complete.
const snapshotTiles = (snapshot, kpis, totals) => [
  { label: "Total CacheValue", value: fmtMoneyCompact(snapshot.totalCacheValue), sub: change30d(snapshot.cacheValueDelta30d, fmtMoneyCompact) },
  { label: "Total NILx value", value: fmtMoneyCompact(snapshot.nilxPool), sub: plural(totals.activated ?? 0, "active profile") },
  snapshot.record
    ? { label: "W-L record", value: snapshot.record, sub: snapshot.rpiRank ? `#${snapshot.rpiRank} RPI` : null }
    : { label: "Profiles complete", value: `${snapshot.profilesComplete ?? 0} of ${totals.total ?? 0}`, sub: "at 100% completion" },
  { label: "Total social reach", value: fmtCompact(snapshot.totalReach), sub: plural(kpis.readinessLinked ?? 0, "linked profile") },
];

// Phones (2406:932): a 2 x 2 grid of borderless grey tiles and an
// "Open Team" text link.
const SnapshotTilePhone = ({ label, value, sub }) => (
  <div className="flex min-w-0 flex-col gap-1 break-words rounded-[12px] bg-bm-subtle p-3">
    <Overline>{label}</Overline>
    <div className="bm-num-m text-bm-ink">{value}</div>
    {sub && <div className="bm-body-xs text-bm-muted">{sub}</div>}
  </div>
);

export const TeamSnapshotCard = ({ snapshot, kpis, totals }) => {
  const phone = usePhoneTheme();
  const tiles = snapshotTiles(snapshot, kpis, totals);
  if (phone) {
    return (
      <section aria-label="Team snapshot" className={cx(PHONE_CARD, "flex min-w-0 flex-col gap-3 py-4")}>
        <Overline>Team snapshot</Overline>
        <div className="grid grid-cols-2 gap-[10px]">
          {tiles.map((t) => (
            <SnapshotTilePhone key={t.label} label={t.label} value={t.value} sub={t.sub} />
          ))}
        </div>
        <div>
          <TextLink to="/coach/team">Open Team</TextLink>
        </div>
      </section>
    );
  }
  return (
    <Card as="section" aria-label="Team snapshot" padding="p-[22px]" className="flex min-w-0 flex-col gap-[10px]">
      <Overline>Team snapshot</Overline>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {tiles.map((t) => (
          <Tile key={t.label} label={t.label} value={t.value} sub={t.sub ? <Tertiary>{t.sub}</Tertiary> : null} />
        ))}
      </div>
      <div>
        <Btn variant="secondary" size="sm" to="/coach/team">
          Open Team
        </Btn>
      </div>
    </Card>
  );
};

const AttentionRow = ({ item, busy, onNotify }) => (
  <li className="flex items-center justify-between gap-4 border-b border-bm-mutedbg py-[14px]">
    <div className="flex min-w-0 flex-col gap-[2px]">
      <div className="bm-h-xs text-bm-ink">{item.title}</div>
      <div className="bm-body-m text-bm-muted">{item.meta}</div>
    </div>
    {item.href ? (
      <Btn variant="secondary" size="sm" to={item.href}>
        {item.action}
      </Btn>
    ) : (
      <Btn variant="secondary" size="sm" onClick={() => onNotify(item)} disabled={busy}>
        {busy ? "Opening..." : item.action}
      </Btn>
    )}
  </li>
);

// The 8px tone dot of a phone attention row, from the item kind (D-c4):
// approvals orange, deadlines yellow, compliance flags red, contracts ink.
// The team rows after them (incomplete profiles, the deal overview) are
// tertiary grey: they inform rather than warn.
const ATTENTION_TONES = {
  approval: "bg-bm-orange",
  deadline: "bg-bm-attention",
  compliance: "bg-bm-critical",
  contract: "bg-bm-ink",
  profiles: "bg-bm-tertiary",
  overview: "bg-bm-tertiary",
};

// Phones (2406:1036): the tone dot beside the title, the meta line, then
// the action as a text link (a link when the item has a page, else the
// reminder draft, with the same busy text).
const AttentionRowPhone = ({ item, busy, onNotify }) => (
  <li className="flex flex-col gap-2 border-b border-bm-mutedbg py-[14px] last:border-b-0">
    <div className="flex items-center gap-[10px]">
      <span aria-hidden="true" className={cx("h-2 w-2 shrink-0 rounded-full", ATTENTION_TONES[item.kind] || "bg-bm-tertiary")} />
      <p className="min-w-0 break-words bm-h-xs text-bm-ink">{item.title}</p>
    </div>
    <p className="break-words bm-body-s text-bm-muted">{item.meta}</p>
    <div>
      {item.href ? (
        <TextLink to={item.href}>{item.action}</TextLink>
      ) : (
        <TextLink onClick={() => onNotify(item)} busy={busy}>
          {busy ? "Opening..." : item.action}
        </TextLink>
      )}
    </div>
  </li>
);

// The attention card's element id, the target of the header mail badge
// (/coach/home#attention).
export const ATTENTION_ID = "attention";

const EMPTY_ATTENTION = "Nothing needs you right now. Offers, deadlines, clashes and incomplete profiles on your roster land here.";

// Phones (2406:1003): the head sits above the card, and the section keeps
// the #attention id with a 72px scroll margin so the sticky top bar does
// not cover it.
const AttentionPhone = ({ items, busyId, onNotify }) => (
  <section id={ATTENTION_ID} aria-label="Needs your attention" className="flex min-w-0 scroll-mt-[72px] flex-col gap-[10px]">
    <div className="flex items-center justify-between gap-3">
      <Overline>Needs your attention</Overline>
      <span className="bm-body-xs text-bm-muted">Sorted by urgency</span>
    </div>
    <div className={cx(PHONE_CARD, "py-1")}>
      {items.length ? (
        <ul>
          {items.map((item) => (
            <AttentionRowPhone key={item.id} item={item} busy={busyId === item.id} onNotify={onNotify} />
          ))}
        </ul>
      ) : (
        <p className="py-6 text-center bm-body-m text-bm-muted">{EMPTY_ATTENTION}</p>
      )}
    </div>
  </section>
);

// `busyId`: the item whose reminder is being logged. Rows are keyed by the
// item id, since two deals can give the same title.
export const AttentionCard = (props) => {
  const phone = usePhoneTheme();
  if (phone) return <AttentionPhone {...props} />;
  const { items, busyId, onNotify } = props;
  return (
    <Card as="section" id={ATTENTION_ID} aria-label="Needs your attention" padding="p-[22px]" className="flex min-w-0 flex-1 flex-col gap-[14px]">
      <div className="flex items-center justify-between pb-[6px]">
        <Overline>Needs your attention</Overline>
        <span className="bm-body-s text-bm-tertiary">Sorted by urgency</span>
      </div>
      {items.length ? (
        <ul>
          {items.map((item) => (
            <AttentionRow key={item.id} item={item} busy={busyId === item.id} onNotify={onNotify} />
          ))}
        </ul>
      ) : (
        <p className="py-6 text-center bm-body-m text-bm-muted">{EMPTY_ATTENTION}</p>
      )}
    </Card>
  );
};

// The compact InsightBox of the compliance column: 16px icon, overline title.
// Phones use it as is (2406:1069).
const CompactInsight = ({ title, children }) => (
  <div className="flex flex-col gap-3 rounded-[8px] border border-bm-border bg-white px-5 py-[18px]">
    <div className="flex items-center gap-[10px]">
      <img src={iconInsight} alt="" className="h-4 w-4" />
      <Overline>{title}</Overline>
    </div>
    <p className="bm-body-m text-bm-muted">{children}</p>
  </div>
);

export const ComplianceColumn = ({ compliance }) => {
  const phone = usePhoneTheme();
  const tracked = Boolean(compliance.tracked);
  const rows = [
    ["Compliant deals", tracked ? `${compliance.compliant} / ${compliance.tracked}` : EMPTY_VALUE, tracked ? "text-bm-positive" : "text-bm-ink", "bm-num-m"],
    ["Average deal value", fmtMoney(compliance.avgDealValue), "text-bm-ink", "bm-num-m"],
    ["Most active platform", compliance.mostActivePlatform || EMPTY_VALUE, "text-bm-ink", "bm-label-l"],
  ];
  if (phone) {
    // 2406:1076: muted labels, bm-label-l values, rules between the rows.
    return (
      <div className="flex w-full flex-col gap-5">
        <CompactInsight title="Compliance summary">{compliance.text}</CompactInsight>
        <section aria-label="Compliance at a glance" className={cx(PHONE_CARD, "flex flex-col pb-[6px] pt-4")}>
          <Overline>Compliance at a glance</Overline>
          {rows.map(([label, value, tone], i) => (
            <div key={label} className={cx("flex items-center justify-between gap-3 py-3", i < rows.length - 1 && "border-b border-bm-mutedbg")}>
              <span className="bm-body-m text-bm-muted">{label}</span>
              <span className={cx("min-w-0 break-words text-right bm-label-l", tone)}>{value}</span>
            </div>
          ))}
        </section>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col gap-3 lg:w-[420px] lg:shrink-0">
      <CompactInsight title="Compliance summary">{compliance.text}</CompactInsight>
      <Card as="section" aria-label="Compliance at a glance" padding="p-4" className="flex flex-col gap-3 !rounded-[8px]">
        <Overline>Compliance at a glance</Overline>
        {rows.map(([label, value, tone, type]) => (
          <div key={label} className="flex items-center justify-between gap-3">
            <span className="bm-label-l text-bm-ink">{label}</span>
            <span className={`${type} ${tone}`}>{value}</span>
          </div>
        ))}
      </Card>
    </div>
  );
};
